import { useContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { FormFieldLayout } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { State } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridHeaderSelectionChangeEvent,
  GridPageChangeEvent,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid'
import { Upload } from 'antd'
import { LabeledValue } from 'antd/es/select'
import { SymbolDto } from 'api/MetaTrader/SymbolDto'
import { PlatformTypeId } from 'api/Trading-Accounts/Types/PlatformTypeDto'
import { ServerType } from 'api/Trading-Accounts/Types/ServerType'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Modal } from 'components/CustomModal'
import { FormBaseButtonContainer } from 'components/Form/Form.styles'
import { Loading } from 'components/Loading'
import MoreButton from 'components/MoreButton/MoreButton'
import { MultiSelect } from 'components/Select'
import { errorToast, successToast, ToastContext } from 'components/Toast/Toast'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchServerAction } from 'store/trading-accounts/trading-accounts.actions'
import { useApiClient } from 'utils/api/ApiClient'
import {
  isMT4PlatformType,
  isMT5PlatformType,
  MetaTraderApiClient,
  PlatformType,
} from 'utils/api/metaTraderApi'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { links } from 'utils/featureToggles'
import stringComparator from 'utils/stringComparator'

import ClientAreaAuditLogModal from '../../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import { BrokeragePlatformSwapsForm, EditSymbolFormValues } from './BrokeragePlatformSwapsForm'

const createDataState = (state: State, data: SymbolDto[]) => ({
  result: data
    .slice(state.skip, (state.take as number) + (state.skip as number))
    .sort((a, b) => stringComparator(a.name, b.name)),
  dataState: state,
})

export type UpdateSwapServerValues = {
  servers: string[]
  symbols: SymbolDto[]
}
export const BrokeragePlatformSwapsPage = (): JSX.Element => {
  const location = useLocation()
  const type =
    location.pathname === links.brokeragePlatformsMT4Swaps.url
      ? PlatformType.metatrader4
      : PlatformType.metatrader5
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const metaTraderApiClient = useApiClient(MetaTraderApiClient)
  const setToast = useContext(ToastContext)

  const dispatch = useAppDispatch()

  const [auditLogModal, setAuditLogModal] = useState<{ visible: boolean; data?: SymbolDto }>({
    visible: false,
  })

  const platformServers = useAppSelector((state) => state.tradingAccounts.platformServers)

  const [sourceServer, setSourceServer] = useState<ServerType>()
  const [targetServers, setTargetServers] = useState<ServerType[]>([])
  const [editSymbolModal, setEditSymbolModal] = useState<{ visible: boolean; item?: SymbolDto }>({
    visible: false,
  })
  const [confirmationModal, setConfirmationModal] = useState<boolean>(false)

  const [dataState, setDataState] = useState<{ result: SymbolDto[]; dataState: State }>(
    createDataState(
      {
        take: 1000,
        skip: 0,
      },
      []
    )
  )

  const { callback: fetchData, forceRefresh } = useCallbackWithForceRefresh(async () => {
    if (!sourceServer) return []
    return metaTraderApiClient.getSymbols(type, sourceServer.id)
  }, [sourceServer])

  const { data, isLoading } = useFetchOne(fetchData)

  useEffect(() => {
    setTargetServers([])
    setSourceServer(undefined)
  }, [type])

  useEffect(() => {
    setDataState(createDataState(dataState.dataState, data || []))
  }, [data, dataState.dataState])

  useEffect(() => {
    if (type === PlatformType.metatrader4)
      dispatch(fetchServerAction(PlatformTypeId.MT4, tradingAccountsApiClient))
    if (type === PlatformType.metatrader5)
      dispatch(fetchServerAction(PlatformTypeId.MT5, tradingAccountsApiClient))
  }, [type]) // eslint-disable-line react-hooks/exhaustive-deps

  const selectionChange = (event: GridSelectionChangeEvent) => {
    const results = dataState.result.map((item) => {
      if (item.name === event.dataItem.name) {
        item.selected = !event.dataItem.selected
      }
      return item
    })
    setDataState(createDataState(dataState.dataState, results))
  }

  const pageChange = (event: GridPageChangeEvent) => {
    if (data)
      setDataState(
        createDataState(
          {
            take: event.page.take,
            skip: event.page.skip,
          },
          data
        )
      )
  }

  const getSelectedItems = () => {
    return dataState.result.filter((taDto) => taDto.selected)
  }

  const onEditSymbol = (values: EditSymbolFormValues) => {
    const filteredState = dataState.result.filter((d) => d.name !== values.name)
    const newData = [...filteredState, values]
    setDataState(createDataState(dataState.dataState, newData))
    setEditSymbolModal({ item: undefined, visible: false })
  }

  const handleHeaderSelectionChange = (event: GridHeaderSelectionChangeEvent<HTMLInputElement>) => {
    const checkboxElement = event.syntheticEvent.target
    const isChecked = checkboxElement.checked
    const selected = (dataState.result || []).map((item) => {
      return { ...item, selected: isChecked }
    })
    setDataState(createDataState(dataState.dataState, selected))
  }

  const headerSelectionValue = (): boolean => {
    if ((dataState.result || []).length <= 0) {
      return false
    }
    return (dataState.result || []).findIndex((dataItem) => !dataItem.selected) === -1
  }

  const handleEdit = (item: SymbolDto) => {
    setEditSymbolModal({ visible: true, item })
  }

  const getServersByPlatformType = () => {
    if (type === PlatformType.metatrader4) {
      return platformServers.filter((s) => s?.platformType?.id === 1)
    }
    return platformServers.filter((s) => s?.platformType?.id === 2)
  }

  const onSubmit = async () => {
    const selected = getSelectedItems()

    if (selected.length === 0) {
      return setToast(errorToast('You must select at least one item to update a server.'))
    }
    if (!targetServers || targetServers.length < 1) {
      return setToast(errorToast('You must select the servers!'))
    }
    const updateSwapServerValues: UpdateSwapServerValues = {
      servers: targetServers.map((server) => server.id),
      symbols: selected,
    }
    try {
      await metaTraderApiClient.updatePlatformSwaps(type, updateSwapServerValues)
      setToast(successToast('Servers updated.'))
    } catch (e) {
      console.error(e)
    }

    setConfirmationModal(false)
    forceRefresh()
  }

  const setAuditLog = (symbolDto: SymbolDto) => {
    setAuditLogModal({
      data: {
        ...symbolDto,
        name: symbolDto.name.replace('#', '%23'),
      },
      visible: true,
    })
  }

  const handleTargetServerChange = (targetServerIdList: LabeledValue[]) => {
    let newTargetServers = []
    newTargetServers = targetServerIdList.map((server: LabeledValue) =>
      (platformServers || []).find((pl) => pl.id === server.value)
    )

    if (newTargetServers) setTargetServers(newTargetServers as ServerType[])
  }

  return (
    <Loading isLoading={isLoading}>
      {confirmationModal && (
        <ConfirmationModal
          contentText={`Are you sure you want to update long/short swaps to serverIds: ${targetServers?.map(
            (server) => server.id
          )}`}
          yesButtonText='Yes, update'
          onYes={onSubmit}
          onCancel={() => setConfirmationModal(false)}
          visible={confirmationModal}
        />
      )}
      {auditLogModal.visible && auditLogModal.data && (
        <ClientAreaAuditLogModal
          isVisible={auditLogModal.visible}
          onClose={() => setAuditLogModal({ visible: false })}
          {...(isMT4PlatformType(type) && sourceServer
            ? {
                mt4Configuration: {
                  serverId: sourceServer.id,
                  symbolId: auditLogModal.data.name,
                },
              }
            : {})}
          {...(isMT5PlatformType(type) && sourceServer
            ? {
                mt5Configuration: {
                  serverId: sourceServer.id,
                  symbolId: auditLogModal.data.name,
                },
              }
            : {})}
        />
      )}
      {editSymbolModal.visible && editSymbolModal.item && (
        <Modal
          visible={editSymbolModal.visible}
          onClose={() => setEditSymbolModal({ visible: false, item: undefined })}
        >
          <BrokeragePlatformSwapsForm onSubmit={onEditSymbol} data={editSymbolModal.item} />
        </Modal>
      )}
      <FormFieldLayout label='Source Server'>
        <MultiSelect
          placeholder='Select Source Server'
          value={sourceServer?.id}
          allowClear
          size={'lg'}
          className='w-52'
          onChange={(v) => {
            const serverById = (platformServers || []).find((pl) => pl.id === v.id)
            if (serverById) {
              setSourceServer(serverById)
            }
          }}
          options={getServersByPlatformType().map((s) => ({
            label: s.publicName,
            value: s.id,
            id: s.id,
          }))}
        />
      </FormFieldLayout>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Upload
          accept='.csv'
          showUploadList={false}
          beforeUpload={(file) => {
            const reader = new FileReader()
            reader.onload = (e) => {
              const result = e.target?.result
              if (typeof result === 'string') {
                const dataWithBreak = result.split('\n')
                const prepareSymbol: SymbolDto[] = []
                dataWithBreak.forEach((value, index) => {
                  if (index > 0 && value.length > 2) {
                    const spllited = value.split(',').map((x) => x.replace('\r', ''))
                    const newSymbol: SymbolDto = {
                      name: spllited[0],
                      swapLong: Number(spllited[1]),
                      swapShort: Number(spllited[2]),
                    }
                    prepareSymbol.push(newSymbol)
                  }
                })
                setDataState(createDataState(dataState.dataState, prepareSymbol))
              }
            }
            reader.readAsText(file)
            // Prevent upload
            return false
          }}
        >
          <Button type='button'>Upload SWAPS</Button>
        </Upload>
      </div>
      <Grid
        style={{ zIndex: 0 }}
        resizable
        take={dataState.dataState.take}
        skip={dataState.dataState.skip}
        total={data?.length}
        pageSize={dataState.dataState.take}
        data={dataState.result}
        selectedField='selected'
        onSelectionChange={selectionChange}
        onHeaderSelectionChange={handleHeaderSelectionChange}
        onPageChange={pageChange}
        sortable
        pageable={{ pageSizes: [500, 750, 1000, 2000] }}
        data-test='table'
      >
        <Column field='selected' width='50px' headerSelectionValue={headerSelectionValue()} />
        <Column className='table-columns__content' field='name' title='Symbol' />
        <Column className='table-columns__number' field='swapLong' title='SWAP Long' />
        <Column className='table-columns__number' field='swapShort' title='SWAP Short' />
        <Column
          width={50}
          title=''
          cell={(props: GridCellProps) => (
            <td>
              <MoreButton
                items={[
                  {
                    name: 'Edit',
                    'data-test': 'edit',
                    onClick: () => handleEdit(props.dataItem),
                  },
                  {
                    name: 'Audit log',
                    'data-test': 'audit_log',
                    onClick: () => setAuditLog(props.dataItem),
                  },
                ]}
                data-test='more-button'
              />
            </td>
          )}
        />
      </Grid>
      <FormFieldLayout label='Target Server'>
        <MultiSelect
          labelInValue
          placeholder='Select Target Server'
          value={
            targetServers
              ? targetServers.map((s) => ({
                  label: s.publicName,
                  value: s.id,
                  id: s.id,
                }))
              : []
          }
          allowClear
          size={'lg'}
          className='w-52'
          onChange={(e) => handleTargetServerChange(e)}
          options={getServersByPlatformType().map((s) => ({
            label: s.publicName,
            value: s.id,
            id: s.id,
          }))}
        />
      </FormFieldLayout>
      <FormBaseButtonContainer>
        <Button
          type='submit'
          disabled={!targetServers}
          onClick={() => setConfirmationModal(true)}
          className='button'
        >
          Update
        </Button>
      </FormBaseButtonContainer>
    </Loading>
  )
}
